import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';

// EVENT GENERAL PROPERTIES
// ============================================================
type EventProps = Record<string, string | number | string[] | number[]>;

const getSubdomainName = function getSubdomainName(): string {
  const subdomainParts = window.location.hostname.split('.');

  if (subdomainParts.length > 2) {
    subdomainParts.shift();
  }

  return `.${subdomainParts.join('.')}`;
};

const COOKIE_DOMAIN = getSubdomainName();

const detector = UAParser(window.navigator.userAgent);

const COOKIE_LAST_EVENT_TIMESTAMP = 'ks_last_event_timestamp';

const setEventTimestamp = function setEventTimestamp() {
  const timestamp = Date.now().toString();

  Cookies.set(COOKIE_LAST_EVENT_TIMESTAMP, timestamp, {
    expires: 365,
    domain: COOKIE_DOMAIN,
  });
};

const getVisitorId = function getVisitorUUID(): string {
  const COOKIE_NAME = 'ks_visitor_id';
  const visitorId = Cookies.get(COOKIE_NAME);

  if (visitorId) {
    return visitorId;
  }

  const newUdid = 'randomUUID' in crypto ? crypto.randomUUID() : '';

  Cookies.set(COOKIE_NAME, newUdid, {
    expires: 365,
    domain: COOKIE_DOMAIN,
  });

  return newUdid;
};

const COOKIE_EVENT_ID_NAME = 'ks_event_id';

const setEventId = function setEventId(id: number) {
  Cookies.set(COOKIE_EVENT_ID_NAME, id.toString(), {
    expires: 365,
    domain: COOKIE_DOMAIN,
  });
};

const getEventId = function getEventId(): number {
  const cookieValue = Number(Cookies.get(COOKIE_EVENT_ID_NAME) ?? '0');
  const prevEventId = Number.isNaN(cookieValue) ? 0 : cookieValue;
  const nextEventId = prevEventId + 1;

  setEventId(nextEventId);

  return nextEventId;
};

const COOKIE_SESSION_NUMBER_NAME = 'ks_session_number';

const incrementSessionNumber = function incrementSessionNumber() {
  const cookieValue = Number(Cookies.get(COOKIE_SESSION_NUMBER_NAME) ?? '0');
  const prevSessionNumber = Number.isNaN(cookieValue) ? 0 : cookieValue;
  const nextSessionNumber = prevSessionNumber + 1;

  Cookies.set(COOKIE_SESSION_NUMBER_NAME, nextSessionNumber.toString(), {
    expires: 365,
    domain: COOKIE_DOMAIN,
  });
};

const getSessionNumber = function getSessionNumber(): number {
  const cookieValue = Number(Cookies.get(COOKIE_SESSION_NUMBER_NAME) ?? '0');
  const sessionNumber = Number.isNaN(cookieValue) ? 0 : cookieValue;

  if (sessionNumber === 0) {
    incrementSessionNumber();

    return 1;
  }

  return sessionNumber;
};

const COOKIE_SESSION_DATA_NAME = 'ks_session_data';

type UniqueSession = {
  id: string;
  started: number;
};

const updateSessionData = function updateSessionData(): UniqueSession {
  const startedTimestamp = Date.now();
  const sessionId = 'randomUUID' in crypto ? crypto.randomUUID() : '';

  const data: UniqueSession = {
    id: sessionId,
    started: startedTimestamp,
  };

  Cookies.set(COOKIE_SESSION_DATA_NAME, JSON.stringify(data), {
    expires: 365,
    domain: COOKIE_DOMAIN,
  });

  return data;
};

const getSessionData = function getSessionData(): UniqueSession {
  let sessionData: UniqueSession;

  try {
    const cookieValue = Cookies.get(COOKIE_SESSION_DATA_NAME) ?? '{}';
    const cookieData = JSON.parse(cookieValue) as UniqueSession;

    if ('id' in cookieData && 'started' in cookieData) {
      sessionData = cookieData;
    } else {
      throw new Error('can`t parse COOKIE_SESSION_DATA_NAME');
    }
  } catch (_error) {
    sessionData = updateSessionData();
  }

  return sessionData;
};

const getEventGeneralProps = function getEventGeneralProps(): EventProps {
  const userId = Cookies.get('ks_user_id'); // created by backend for logged users
  const timeZoneOffset =
    new Date().toString().match(/([-+][0-9]+)\s/)?.[1] ?? '';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const sessionNumber = getSessionNumber();
  const isFirstSession = sessionNumber === 1 ? 1 : 0;
  const sessionData = getSessionData();

  const browserName =
    detector.browser.name && detector.browser.version
      ? `${detector.browser.name} ${detector.browser.version}`
      : 'unknown';

  const currentUrl = new URL(window.location.href);
  const deviceId = currentUrl.searchParams.get('udid') ?? '';

  const getVisitorCountry = function getVisitorCountry(): string {
    const COOKIE_VISITOR_COUNTRY_NAME = 'visitor_country';
    const cookieValue = Cookies.get(COOKIE_VISITOR_COUNTRY_NAME) ?? '';
    const visitorCountry = cookieValue.trim();

    return visitorCountry;
  };

  const visitorCountry = getVisitorCountry();

  const getGaCookie = function getGaCookie(): string {
    const GA_COOKIE_NAME = '_ga_E1HB7EM8VF';
    const cookieValue = Cookies.get(GA_COOKIE_NAME) ?? '';
    const gaCookie = cookieValue.trim();

    return gaCookie;
  };

  const gaCookie = getGaCookie();

  return {
    user_id: userId ?? '',
    visitor_id: getVisitorId(),
    event_id: getEventId(),
    event_time_utc: Date.now(),
    time_offset: timeZoneOffset,
    time_zone: timeZone,
    session_number: sessionNumber,
    session_id: sessionData.id,
    session_started: sessionData.started,
    is_first_session: isFirstSession,
    platform: 'web',
    device_category: detector.device.type ?? 'desktop',
    os_name: detector.os.name ?? 'unknown',
    os_version: detector.os.version ?? 'unknown',
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    browser_group: detector.browser.name ?? 'unknown',
    browser_name: browserName,
    browser_user_agent: navigator.userAgent,
    app_language: document.documentElement.lang,
    event_source: 'web',
    initial_page_link: document.referrer,
    page_name: window.location.pathname,
    page_link: window.location.href,
    device_id: deviceId,
    cloudflare_country: visitorCountry,
    ga_visitor_id: gaCookie,
  };
};

// KINESIS EVENT
// ============================================================
const kinesisClientSend = function kinesisClientSend(
  recordData: EventProps | EventProps[],
) {
  if (localStorage?.getItem('debug_kinesis')) {
    if (Array.isArray(recordData)) {
      recordData.forEach((item) => {
        // eslint-disable-next-line no-console
        console.log(item.event_name, JSON.stringify(item));
      });
    } else {
      // eslint-disable-next-line no-console
      console.log(recordData.event_name, JSON.stringify(recordData));
    }
  }

  const LAMBDA_FUNCTION_URL = import.meta.env.VITE_KINESIS_LAMBDA_FUNCTION_URL;

  if (!LAMBDA_FUNCTION_URL) {
    return;
  }

  fetch(LAMBDA_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(recordData),
  }).catch(() => {});
};

export const ksEvent = function kinesisEvent(
  eventName: string,
  eventProps: EventProps = {},
) {
  setTimeout(() => {
    const generalProps = getEventGeneralProps();

    const recordData = {
      ...generalProps,
      ...eventProps,
      event_name: eventName,
    };

    setEventTimestamp();

    kinesisClientSend(recordData);
  }, 0);
};

// SESSION START
// ============================================================
const ksEventSessionStart = function sendSessionStartEvent(
  eventProps: EventProps = {},
) {
  const SESSION_DURATION = 10 * 60 * 1000; // 10min
  const DEFAULT_VALUE = '0';
  const lastEventCookie =
    Cookies.get(COOKIE_LAST_EVENT_TIMESTAMP) ?? DEFAULT_VALUE;

  const lastEventTimestamp = Number(lastEventCookie);

  const isNewSession =
    Number.isNaN(lastEventTimestamp) ||
    Date.now() - lastEventTimestamp > SESSION_DURATION;

  if (isNewSession) {
    setEventId(0);
    updateSessionData();
    incrementSessionNumber();
    ksEvent('session_start', eventProps);
  }

  if (lastEventCookie === DEFAULT_VALUE) {
    ksEvent('first_launch', eventProps);
  }
};

export function initKinesis() {
  ksEventSessionStart();
  ksEvent('product_page_visited', { page_group: 'product' });
}
